import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, tokens } from "./api";
import type { Role, User } from "./types";

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (payload: {
    username: string;
    password: string;
    email: string;
    display_name: string;
    discipline: string;
  }) => Promise<void>;
  logout: () => void;
  setTheme: (theme: "dark" | "light") => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside an AuthProvider");
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const applyTheme = (theme: "dark" | "light") => {
    document.documentElement.dataset.theme = theme;
  };

  const loadUser = useCallback(async () => {
    if (!tokens.access) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api.get<User>("/api/auth/me/");
      setUser(me);
      applyTheme(me.theme);
    } catch {
      tokens.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Paint the stored theme before the profile arrives, so a reload does not
    // flash the wrong palette.
    const stored = localStorage.getItem("evolog.theme");
    applyTheme(stored === "dark" ? "dark" : "light");
    void loadUser();
  }, [loadUser]);

  const login = useCallback(
    async (username: string, password: string) => {
      const data = await api.post<{ access: string; refresh: string }>("/api/auth/token/", {
        username,
        password,
      });
      tokens.set(data.access, data.refresh);
      await loadUser();
    },
    [loadUser],
  );

  const register = useCallback(
    async (payload: {
      username: string;
      password: string;
      email: string;
      display_name: string;
      discipline: string;
    }) => {
      await api.post("/api/auth/register/", payload);
      await login(payload.username, payload.password);
    },
    [login],
  );

  const logout = useCallback(() => {
    tokens.clear();
    setUser(null);
  }, []);

  const setTheme = useCallback(
    (theme: "dark" | "light") => {
      applyTheme(theme);
      localStorage.setItem("evolog.theme", theme);
      setUser((current) => (current ? { ...current, theme } : current));
      // Persisted per user so the choice follows them to another machine.
      if (user) void api.patch("/api/auth/me/", { theme }).catch(() => undefined);
    },
    [user],
  );

  const value = useMemo(
    () => ({ user, loading, login, register, logout, setTheme, refreshUser: loadUser }),
    [user, loading, login, register, logout, setTheme, loadUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* --------------------------------------------------- role helpers (UI only)
 * These mirror the server's rules so the interface can hide what a person
 * cannot do. The server enforces them regardless — this is affordance, not
 * security.
 */

export const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  petrophysicist: "Petrophysicist",
  interpreter: "Interpreter",
  viewer: "Viewer",
};

export const canWrite = (role: Role | null | undefined): boolean =>
  role === "owner" || role === "petrophysicist" || role === "interpreter";

export const canCompute = (role: Role | null | undefined): boolean =>
  role === "owner" || role === "petrophysicist";

export const canAdminister = (role: Role | null | undefined): boolean => role === "owner";
